import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr, formatAUD } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft, Check, X, Download,
  AlertTriangle, RefreshCw, ShieldAlert, ListChecks, ClipboardCheck, Sparkles,
} from "lucide-react";

const STEPS = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Preview" },
  { id: 3, label: "Map Columns" },
  { id: 4, label: "Validate" },
  { id: 5, label: "Compare" },
  { id: 6, label: "Approve" },
  { id: 7, label: "Results" },
];

export default function MaterialsImportPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [session, setSession] = useState(null);
  const [fields, setFields] = useState(null);
  const [compared, setCompared] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [report, setReport] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/materials/import/fields").then((r) => setFields(r.data)).catch(() => {});
  }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!(file.name.toLowerCase().endsWith(".csv") || file.name.toLowerCase().endsWith(".xlsx"))) {
      toast.error("Only .csv or .xlsx files are supported");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/materials/import/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSession(data);
      setStep(2);
      toast.success(`${data.row_count} rows parsed`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); }
  };

  const setMapping = async (m) => {
    if (!session) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/materials/import/${session.id}/mapping`, { mapping: m });
      setSession({ ...data, rows: session.rows });
      setCompared(data.compared || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/materials/import/${session.id}/approve`);
      setReport(data);
      setStep(7);
      toast.success(`Import complete — ${data.created} new, ${data.updated} updated`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const downloadErrors = async () => {
    try {
      const r = await api.get(`/materials/import/${session.id}/errors.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `import_errors_${session.id}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const uploadImagesZip = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please choose a .zip file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/materials/import/${session.id}/images-zip`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSession((s) => ({ ...s, images_zip_name: file.name, images_zip_count: data.entry_count }));
      toast.success(`${data.entry_count} images ready to attach`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (session?.id && session.status !== "committed") {
      try { await api.delete(`/materials/import/${session.id}`); } catch {}
    }
    nav("/materials");
  };

  const summary = session?.summary;
  const canApprove = !!summary?.can_approve;

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-6xl" data-testid="materials-import-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline">Materials</div>
          <h1 className="font-serif font-light mt-3" style={{ fontSize: "clamp(38px, 5vw, 52px)", lineHeight: 1.05 }}>
            Import supplier catalogue
          </h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Bulk-import materials from a CSV or XLSX file. No changes are saved until you approve the final review.
          </p>
        </div>
        <button onClick={cancel} className="btn-outline text-xs" data-testid="btn-cancel">
          <X className="w-3 h-3" strokeWidth={1.5}/> Cancel
        </button>
      </div>

      <Stepper current={step}/>

      <div className="mt-10">
        {step === 1 && (
          <UploadStep uploading={uploading} onFile={upload} fileRef={fileRef} fields={fields}/>
        )}
        {step === 2 && session && (
          <PreviewStep session={session} onNext={() => setStep(3)}/>
        )}
        {step === 3 && session && fields && (
          <MappingStep
            session={session}
            fields={fields.fields}
            required={fields.required}
            onChange={setMapping}
            onNext={() => setStep(4)}
            busy={busy}
          />
        )}
        {step === 4 && session && (
          <ValidateStep
            session={session}
            compared={compared}
            onBack={() => setStep(3)}
            onNext={() => setStep(5)}
            onDownloadErrors={downloadErrors}
          />
        )}
        {step === 5 && (
          <CompareStep
            compared={compared}
            filter={filter}
            setFilter={setFilter}
            onBack={() => setStep(4)}
            onNext={() => setStep(6)}
          />
        )}
        {step === 6 && session && (
          <ApproveStep
            summary={summary}
            canApprove={canApprove}
            busy={busy}
            session={session}
            onUploadZip={uploadImagesZip}
            onBack={() => setStep(5)}
            onApprove={approve}
          />
        )}
        {step === 7 && report && (
          <ResultsStep report={report} onDone={() => nav("/materials")}/>
        )}
      </div>
    </div>
  );
}

function Stepper({ current }) {
  return (
    <ol className="flex flex-wrap items-center gap-1 text-xs" data-testid="stepper">
      {STEPS.map((s, i) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <li key={s.id} className="flex items-center gap-1" data-testid={`step-${s.id}`}>
            <div
              className={`w-6 h-6 flex items-center justify-center border ${
                done ? "bg-foreground text-background border-foreground"
                     : active ? "border-foreground text-foreground"
                              : "border-input text-muted-foreground"
              }`}
            >
              {done ? <Check className="w-3 h-3" strokeWidth={2}/> : s.id}
            </div>
            <div className={active ? "font-medium" : "text-muted-foreground"}>{s.label}</div>
            {i < STEPS.length - 1 && <ArrowRight className="w-3 h-3 text-muted-foreground mx-1" strokeWidth={1.5}/>}
          </li>
        );
      })}
    </ol>
  );
}

// ── Step components ────────────────────────────────────────────────────
function UploadStep({ uploading, onFile, fileRef, fields }) {
  return (
    <div className="luxe-card p-10 text-center" data-testid="step-upload">
      <FileSpreadsheet className="w-14 h-14 mx-auto text-muted-foreground" strokeWidth={1.5}/>
      <div className="font-serif text-2xl mt-4">Choose a supplier file</div>
      <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
        Upload a CSV or XLSX with your supplier's catalogue. We'll parse it, map the columns to Subtend Studio fields, then let you review every change before anything hits the library.
      </p>
      <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden"
             onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
             data-testid="input-file"/>
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="btn-primary mt-6"
        data-testid="btn-choose-file"
      >
        <Upload className="w-4 h-4" strokeWidth={1.5}/>
        {uploading ? "Reading file…" : "Choose file"}
      </button>
      {fields && (
        <p className="text-[11px] text-muted-foreground mt-4">
          Max {(fields.max_bytes / 1_048_576).toFixed(0)} MB · Up to {fields.max_rows.toLocaleString("en-AU")} rows · Required fields: Name, SKU
        </p>
      )}
    </div>
  );
}

function PreviewStep({ session, onNext }) {
  const rows = session.preview_rows || [];
  return (
    <div data-testid="step-preview">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="overline">{session.filename}</div>
        <div className="text-xs text-muted-foreground">
          {session.file_type.toUpperCase()} · {session.row_count.toLocaleString("en-AU")} data rows · {session.headers.length} columns
        </div>
      </div>
      <div className="luxe-card overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-[color:hsl(var(--surface-panel))]">
            <tr>
              {session.headers.map((h) => (
                <th key={h} className="p-3 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                {session.headers.map((h) => (
                  <td key={h} className="p-3 whitespace-nowrap">{String(r[h] ?? "")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-6 flex justify-end">
        <button data-testid="btn-preview-next" onClick={onNext} className="btn-primary">
          Continue to mapping <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
        </button>
      </div>
    </div>
  );
}

function MappingStep({ session, fields, required, onChange, onNext, busy }) {
  const [mapping, setMapping] = useState(session.mapping || {});
  const sessionId = session.id;
  const sessionMapping = session.mapping;
  useEffect(() => setMapping(sessionMapping || {}), [sessionId, sessionMapping]);

  const setOne = (header, fid) => {
    setMapping((m) => {
      const next = { ...m };
      if (fid) next[header] = fid; else delete next[header];
      return next;
    });
  };
  const mappedRequired = new Set(Object.values(mapping));
  const missingRequired = required.filter((r) => !mappedRequired.has(r));

  return (
    <div data-testid="step-mapping">
      <p className="text-sm text-muted-foreground mb-4">
        Map each column in your file to a Subtend Studio field. We've auto-matched what we can — please confirm.
      </p>
      <div className="luxe-card">
        <table className="min-w-full text-xs">
          <thead className="bg-[color:hsl(var(--surface-panel))]">
            <tr>
              <th className="p-3 text-left">Column</th>
              <th className="p-3 text-left">Sample value</th>
              <th className="p-3 text-left">Maps to</th>
            </tr>
          </thead>
          <tbody>
            {session.headers.map((h) => {
              const sample = (session.preview_rows?.[0] || {})[h] || (session.preview_rows?.[1] || {})[h] || "";
              return (
                <tr key={h} className="border-t border-border" data-testid={`map-row-${h}`}>
                  <td className="p-3 font-medium whitespace-nowrap">{h}</td>
                  <td className="p-3 text-muted-foreground max-w-xs truncate">{String(sample)}</td>
                  <td className="p-3">
                    <select
                      className="luxe-input"
                      value={mapping[h] || ""}
                      onChange={(e) => setOne(h, e.target.value)}
                      data-testid={`select-map-${h}`}
                    >
                      <option value="">— Ignore this column —</option>
                      {fields.map((f) => (
                        <option key={f.id} value={f.id}>{f.label}{f.required ? " *" : ""}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {missingRequired.length > 0 && (
        <div className="mt-4 text-xs border-l-2 border-[color:hsl(var(--destructive))] pl-3 py-1" data-testid="mapping-missing">
          Required field(s) still unmapped: {missingRequired.join(", ")}
        </div>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={async () => { await onChange(mapping); onNext(); }}
          disabled={busy || missingRequired.length > 0}
          className="btn-primary"
          data-testid="btn-mapping-next"
        >
          {busy ? "Validating…" : "Validate rows"}
          <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
        </button>
      </div>
    </div>
  );
}

function ValidateStep({ session, compared, onBack, onNext, onDownloadErrors }) {
  const summary = session.summary || {};
  const errorRows = compared.filter((r) => r.status === "error");
  const warnRows = compared.filter((r) => (r.warnings || []).length > 0);
  return (
    <div data-testid="step-validate">
      <div className="grid sm:grid-cols-4 gap-3">
        <StatCard label="Total rows" value={summary.total || 0} icon={ListChecks}/>
        <StatCard label="Errors" value={summary.error || 0} icon={ShieldAlert} tone="destructive" testid="stat-errors"/>
        <StatCard label="Warnings" value={summary.warnings || 0} icon={AlertTriangle} tone="accent"/>
        <StatCard label="Ready to import" value={(summary.new || 0) + (summary.changed || 0)} icon={Check}/>
      </div>

      {errorRows.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <div className="overline">Errors — {errorRows.length}</div>
            <button data-testid="btn-download-errors" onClick={onDownloadErrors} className="btn-outline text-xs">
              <Download className="w-3 h-3" strokeWidth={1.5}/> Download errors as CSV
            </button>
          </div>
          <div className="luxe-card divide-y divide-border">
            {errorRows.slice(0, 30).map((r) => (
              <div key={r.source_row} className="p-3 text-xs">
                <div className="font-medium">Row {r.source_row} · SKU {r.record?.sku || "(missing)"}</div>
                <ul className="mt-1 space-y-0.5" style={{ color: "#B85C38" }}>
                  {r.errors.map((e, i) => <li key={i}>· {e}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {warnRows.length > 0 && errorRows.length === 0 && (
        <div className="mt-6">
          <div className="overline mb-2">Warnings — {warnRows.length}</div>
          <div className="luxe-card divide-y divide-border">
            {warnRows.slice(0, 20).map((r) => (
              <div key={r.source_row} className="p-3 text-xs">
                <div className="font-medium">Row {r.source_row} · SKU {r.record?.sku || "—"}</div>
                <ul className="mt-1 space-y-0.5 text-muted-foreground">
                  {r.warnings.map((w, i) => <li key={i}>· {w}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-between">
        <button onClick={onBack} className="btn-outline"><ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back</button>
        <button
          onClick={onNext}
          disabled={!!(summary.error && summary.error > 0)}
          className="btn-primary"
          data-testid="btn-validate-next"
        >
          {summary.error > 0 ? "Fix errors to continue" : "Compare with library →"}
        </button>
      </div>
    </div>
  );
}

function CompareStep({ compared, filter, setFilter, onBack, onNext }) {
  const filtered = useMemo(() => filter === "all" ? compared : compared.filter((r) => r.status === filter), [compared, filter]);
  const counts = useMemo(() => {
    const c = { all: compared.length, new: 0, changed: 0, unchanged: 0, error: 0 };
    for (const r of compared) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [compared]);

  return (
    <div data-testid="step-compare">
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="compare-filters">
        {["all", "new", "changed", "unchanged", "error"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`px-3 py-1.5 text-xs border ${filter === k ? "bg-foreground text-background border-foreground" : "border-input text-foreground hover:border-foreground"}`}
          >
            {k.charAt(0).toUpperCase() + k.slice(1)} ({counts[k] || 0})
          </button>
        ))}
      </div>

      <div className="luxe-card overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-[color:hsl(var(--surface-panel))]">
            <tr>
              <th className="p-3 text-left w-16">Row</th>
              <th className="p-3 text-left">SKU</th>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Cost</th>
              <th className="p-3 text-left">Changes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 500).map((r) => (
              <tr key={r.source_row} className="border-t border-border" data-testid={`compare-row-${r.source_row}`}>
                <td className="p-3 text-muted-foreground">{r.source_row}</td>
                <td className="p-3 font-mono">{r.record?.sku || "—"}</td>
                <td className="p-3">{r.record?.name || "—"}</td>
                <td className="p-3"><StatusBadge status={r.status}/></td>
                <td className="p-3">
                  {r.record?.cost_price != null ? formatAUD(r.record.cost_price) : "—"}
                </td>
                <td className="p-3">
                  {r.diff?.length > 0 && (
                    <div className="space-y-0.5">
                      {r.diff.map((d) => (
                        <div key={d.field} className="text-[11px]">
                          <span className="text-muted-foreground">{d.field}:</span>{" "}
                          <span className="line-through text-muted-foreground">{fmt(d.before, d.field)}</span>
                          {" → "}
                          <span className="font-medium">{fmt(d.after, d.field)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {r.warnings?.length > 0 && (
                    <div className="text-[11px] text-[color:hsl(var(--accent))] mt-1">
                      ⚠ {r.warnings.join(" · ")}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">Nothing to show for this filter.</div>
        )}
      </div>

      <div className="mt-6 flex justify-between">
        <button onClick={onBack} className="btn-outline"><ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back</button>
        <button onClick={onNext} className="btn-primary" data-testid="btn-compare-next">
          Continue to approve <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
        </button>
      </div>
    </div>
  );
}

function fmt(v, field) {
  if (v == null || v === "") return "—";
  if (field === "cost_price" || field === "unit_price") return formatAUD(v);
  return String(v);
}

function ApproveStep({ summary, canApprove, busy, session, onUploadZip, onBack, onApprove }) {
  const zipRef = useRef();
  return (
    <div className="luxe-card p-10 text-center" data-testid="step-approve">
      <ClipboardCheck className="w-14 h-14 mx-auto text-[color:hsl(var(--accent))]" strokeWidth={1.5}/>
      <div className="font-serif text-2xl mt-4">You're about to import</div>
      <div className="mt-6 grid grid-cols-3 gap-6 max-w-md mx-auto text-left">
        <ApproveStat label="New" value={summary?.new || 0}/>
        <ApproveStat label="Updated" value={summary?.changed || 0}/>
        <ApproveStat label="Unchanged" value={summary?.unchanged || 0}/>
      </div>

      {/* Optional companion ZIP of product images */}
      <div className="mt-8 max-w-lg mx-auto text-left border border-border p-4" data-testid="images-zip-block">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="overline text-[10px]">Product images (optional)</div>
            <div className="text-sm mt-1">
              {session?.images_zip_count > 0
                ? <>Attached <strong>{session.images_zip_count}</strong> image{session.images_zip_count === 1 ? "" : "s"} from <em>{session.images_zip_name}</em></>
                : "Upload a ZIP of product images — we match by explicit filename or SKU."}
            </div>
          </div>
          <input ref={zipRef} type="file" accept=".zip" className="hidden"
                 onChange={(e) => e.target.files?.[0] && onUploadZip(e.target.files[0])}
                 data-testid="input-images-zip"/>
          <button type="button" disabled={busy} onClick={() => zipRef.current?.click()}
                  className="btn-outline text-xs" data-testid="btn-choose-zip">
            <Upload className="w-3 h-3" strokeWidth={1.5}/>
            {session?.images_zip_count > 0 ? "Replace" : "Choose ZIP"}
          </button>
        </div>
        <div className="text-[10px] text-muted-foreground mt-2">
          Rows with an <strong>Image URL</strong> column are also fetched automatically — images are copied into Subtend Studio storage, never hotlinked from the supplier.
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-6 max-w-lg mx-auto">
        Existing project estimates, draft proposals and issued proposals will not be repriced by this import. New material prices are added to the price history only.
      </p>
      <div className="mt-8 flex justify-center gap-3">
        <button onClick={onBack} className="btn-outline"><ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back</button>
        <button
          onClick={onApprove}
          disabled={!canApprove || busy}
          className="btn-primary"
          data-testid="btn-approve"
        >
          {busy ? "Importing…" : "Approve & import"}
          <Check className="w-4 h-4" strokeWidth={1.5}/>
        </button>
      </div>
    </div>
  );
}

function ResultsStep({ report, onDone }) {
  const img = report.images || { attached: 0, failed: 0, skipped: 0, warnings: [] };
  return (
    <div className="luxe-card p-10 text-center" data-testid="step-results">
      <Sparkles className="w-14 h-14 mx-auto" style={{ color: "#A68A64" }} strokeWidth={1.5}/>
      <div className="font-serif text-3xl mt-4">Import complete</div>
      <div className="mt-6 grid grid-cols-4 gap-4 max-w-2xl mx-auto text-left">
        <ApproveStat label="Created" value={report.created}/>
        <ApproveStat label="Updated" value={report.updated}/>
        <ApproveStat label="Unchanged" value={report.unchanged}/>
        <ApproveStat label="Price history rows" value={report.price_history_records}/>
      </div>
      {(img.attached > 0 || img.failed > 0 || (img.warnings || []).length > 0) && (
        <div className="mt-6 max-w-2xl mx-auto text-left border-t border-border pt-4" data-testid="results-images">
          <div className="overline text-[10px] mb-2">Images</div>
          <div className="text-sm">
            <strong>{img.attached}</strong> attached
            {img.failed > 0 && <span className="text-[color:hsl(var(--destructive))] ml-3">{img.failed} failed</span>}
            {img.skipped > 0 && <span className="text-muted-foreground ml-3">{img.skipped} kept existing</span>}
          </div>
          {(img.warnings || []).length > 0 && (
            <ul className="text-xs text-muted-foreground mt-3 space-y-0.5 max-h-32 overflow-y-auto">
              {img.warnings.slice(0, 12).map((w, i) => <li key={i}>· {w}</li>)}
            </ul>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-6 max-w-lg mx-auto">
        The materials library is up to date. No existing proposals or estimates were changed.
      </p>
      <button onClick={onDone} className="btn-primary mt-8" data-testid="btn-done">
        Back to materials
      </button>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone, testid }) {
  const color = tone === "destructive" ? "hsl(var(--destructive))"
              : tone === "accent" ? "hsl(var(--accent))" : "hsl(var(--foreground))";
  return (
    <div className="luxe-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color }}/>
        <div className="overline text-[10px]">{label}</div>
      </div>
      <div className="font-serif text-3xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
function ApproveStat({ label, value }) {
  return (
    <div>
      <div className="overline text-[10px]">{label}</div>
      <div className="font-serif text-3xl mt-1">{value}</div>
    </div>
  );
}
function StatusBadge({ status }) {
  const map = {
    new: { bg: "#3A4D39", label: "New" },
    changed: { bg: "#A68A64", label: "Changed" },
    unchanged: { bg: "#8A8A8A", label: "Unchanged" },
    error: { bg: "#B85C38", label: "Error" },
  };
  const s = map[status] || map.unchanged;
  return (
    <span className="text-[10px] px-2 py-1" style={{ background: s.bg, color: "white", letterSpacing: "0.08em" }}>
      {s.label.toUpperCase()}
    </span>
  );
}
